// src/pages/sales/PageSales.jsx
import { useState, useEffect } from "react";
import { SubDashboard } from "../../components/SubDashboard";
import { fetchHmGrades, fetchTdsList, fetchMsdsList, downloadPdf } from "../../apiService";
import { navIcon } from "../../navConfig";

// ── Sales dashboard ────────────────────────────────────────────────────────────
export function PageSalesDashboard({ setPage }) {
  return (
    <SubDashboard
      title="Sales"
      subtitle="Customer management & sales activities"
      setPage={setPage}
      cards={[
        { id:"crm-accounts",          icon:navIcon("crm-accounts",24),          label:"Customer Information",          desc:"Accounts, contacts & financial 360",              color:"#f0fdfa", accent:"#0d9488" },
        { id:"sales-work-orders",     icon:navIcon("sales-work-orders",24),     label:"Work Orders",                   desc:"Create and manage customer work orders",          color:"#eff6ff", accent:"#2563eb" },
        { id:"sales-visit-reports",   icon:navIcon("sales-visit-reports",24),   label:"Daily Visit Reports",           desc:"Log and review daily customer visits",            color:"#f0fdf4", accent:"#15803d" },
        { id:"sales-pipeline",        icon:navIcon("sales-pipeline",24),        label:"Customer Development Pipeline", desc:"Track opportunities from shortlist to close",     color:"#eef2ff", accent:"#6366f1" },
        { id:"sample-trials",            icon:navIcon("sample-trials",24),            label:"Sample Tracking",               desc:"Log product samples, track trial outcomes",           color:"#f5f3ff", accent:"#7c3aed" },
        { id:"sales-customer-samples",   icon:navIcon("sales-customer-samples",24),   label:"Customer Samples",              desc:"Submit customer samples to R&D, track analysis status", color:"#ecfeff", accent:"#0891b2" },
        { id:"sales-quotations",      icon:navIcon("sales-quotations",24),      label:"Quotations",                    desc:"Create and manage customer quotations",           color:"#fefce8", accent:"#ca8a04" },
        { id:"sales-proforma",        icon:navIcon("sales-proforma",24),        label:"Proforma Invoices",             desc:"Proforma invoices linked to quotations",          color:"#fff7ed", accent:"#c2410c" },
        { id:"customer-complaints",   icon:navIcon("customer-complaints",24),   label:"Customer Complaints",           desc:"Complaints raised by customers",                  color:"#fdf4ff", accent:"#7e22ce" },
        { id:"sales-monthly-reports", icon:navIcon("sales-monthly-reports",24), label:"Monthly Reports",               desc:"Sales performance & activity summaries",          color:"#fdf4ff", accent:"#9333ea" },
        { id:"sales-targets",         icon:navIcon("sales-targets",24),         label:"Sales Targets",                 desc:"Annual value & quantity targets per salesperson",  color:"#f5f3ff", accent:"#7e22ce" },
        { id:"sales-soa",             icon:navIcon("sales-soa",24),             label:"SOA & Payment Follow-ups",      desc:"Statement of account & outstanding follow-ups",   color:"#f0fdf4", accent:"#15803d" },
        { id:"product-catalog",       icon:navIcon("product-catalog",24),       label:"Product Catalog & Details",     desc:"Water based & hot melt adhesive grades, TDS & MSDS", color:"#eff6ff", accent:"#0369a1" },
      ]}
    />
  );
}

// ── Product Catalog dashboard ──────────────────────────────────────────────────
export function PageProductCatalogDashboard({ setPage }) {
  return (
    <SubDashboard
      title="Product Catalog & Details"
      subtitle="Product grades, TDS & MSDS documentation"
      setPage={setPage}
      cards={[
        { id:"sales-wb", icon:navIcon("sales-wb",24), label:"Water Based Adhesives", desc:"Adhesive product catalogue, TDS & MSDS", color:"#eff6ff", accent:"#1d4ed8" },
        { id:"sales-hm", icon:navIcon("sales-hm",24), label:"Hot Melt Adhesives",    desc:"Hot melt grade catalogue, TDS & MSDS",  color:"#fdf4ff", accent:"#7e22ce" },
      ]}
    />
  );
}

// ── Water Based Adhesives ───────────────────────────────────────────────────────
export function PageSalesWaterBased({ fgs, setPage }) {
  const [tdsMap,  setTdsMap]  = useState({});
  const [msdsMap, setMsdsMap] = useState({});

  useEffect(() => {
    fetchTdsList().then(list => {
      const m = {};
      list.forEach(t => { m[t.fgId] = t; });
      setTdsMap(m);
    }).catch(() => {});
    fetchMsdsList().then(list => {
      const m = {};
      list.forEach(t => { m[t.fgId] = t; });
      setMsdsMap(m);
    }).catch(() => {});
  }, []);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Water Based Adhesives</div>
          <div className="page-subtitle">
            {fgs.length} product{fgs.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 140 }}>Product Code</th>
              <th>Application</th>
              <th style={{ textAlign: "center", width: 110 }}>TDS</th>
              <th style={{ textAlign: "center", width: 110 }}>MSDS</th>
            </tr>
          </thead>
          <tbody>
            {fgs.length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", color: "#aaa", padding: "32px 0" }}>
                  No products found.
                </td>
              </tr>
            )}
            {fgs.map(fg => {
              const tds  = tdsMap[fg.id];
              const msds = msdsMap[fg.id];
              return (
                <tr key={fg.id}>
                  <td className="fw-600">{fg.id}</td>
                  <td style={{ color: "#aaa" }}>{fg.application ?? "—"}</td>
                  <td style={{ textAlign: "center" }}>
                    {tds ? (
                      <button className="btn-small"
                        onClick={() => downloadPdf(`/api/qc/tds/${tds.id}/pdf`, `${tds.productCode}_TDS_v${tds.version}.pdf`)}>
                        Get TDS
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: "#d1d5db" }}>Not available</span>
                    )}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {msds ? (
                      <button className="btn-small"
                        onClick={() => downloadPdf(`/api/qc/msds/${msds.id}/pdf`, `${msds.productCode}_MSDS_v${msds.version}.pdf`)}>
                        Get MSDS
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: "#d1d5db" }}>Not available</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Hot Melt Adhesives ─────────────────────────────────────────────────────────
export function PageSalesHotMelt({ setPage }) {
  const [grades,  setGrades]  = useState([]);
  const [msdsMap, setMsdsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    Promise.all([
      fetchHmGrades(),
      fetchMsdsList().catch(() => []),
    ]).then(([gradeData, msdsList]) => {
      setGrades(gradeData.filter(g => g.active));
      // Index by fgId and also by productCode for flexible matching
      const m = {};
      msdsList.forEach(t => {
        m[t.fgId] = t;
        if (t.productCode) m[t.productCode] = t;
      });
      setMsdsMap(m);
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Hot Melt Adhesives</div>
          <div className="page-subtitle">
            {loading ? "Loading…" : `${grades.length} grade${grades.length !== 1 ? "s" : ""}`}
          </div>
        </div>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12, padding: "16px 0" }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 100 }}>Grade</th>
                <th>Application</th>
                <th style={{ textAlign: "center", width: 110 }}>TDS</th>
                <th style={{ textAlign: "center", width: 110 }}>MSDS</th>
              </tr>
            </thead>
            <tbody>
              {grades.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#aaa", padding: "32px 0" }}>
                    No active grades found.
                  </td>
                </tr>
              )}
              {grades.map(g => {
                const msds = msdsMap[g.id] || msdsMap[g.id?.toUpperCase()];
                return (
                  <tr key={g.id}>
                    <td className="fw-600">{g.displayName || g.id}</td>
                    <td>{g.application}</td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{ fontSize: 11, color: "#d1d5db" }}>N/A</span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {msds ? (
                        <button className="btn-small"
                          onClick={() => downloadPdf(`/api/qc/msds/${msds.id}/pdf`, `${msds.productCode}_MSDS_v${msds.version}.pdf`)}>
                          Get MSDS
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: "#d1d5db" }}>Not available</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
